import {axiosApiInstance, IHttpResponse} from 'api';
import {ApiPathsEnum} from 'enums';
import {config} from 'config';
import {del, get, post} from './http-client';
import {
  IFetchFilesAllPagedThunkPayload,
  IFetchResourcesThunkPayload,
  IGetAllFilesApiResponse,
  IGetAllFileTypeOptionsApiResponse,
  IGetFileByIdApiResponse,
  IGetInstrumentsThunkPayload,
  IGetInstrumentsApiResponse,
  IGetResourcesApiResponse,
  IResourceItemBase,
  IUpdateFileResourcePayload,
  IUpdateFileResourceApiResponse,
  IUpdateWebLinkResourcePayload,
  IUpdateWebLinkResourceApiResponse,
  IGetFileByResourceIdApiResponse,
  ICreateShortUrlExpirationApiResponse,
  ICreateShortUrlExpirationPayload,
} from 'models';
import {
  getApiJsonBlobDataHeadersConfig,
  getApiMultipartFormDataHeadersConfig,
  returnQueryStringWithParams,
} from 'utils';

const {jaEeApi: apiConfig} = config;

export const pamApi = {
  // Enumerations

  async getAllFileTypeOptions(): Promise<IHttpResponse<IGetAllFileTypeOptionsApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.HELPERS}/GetAllFileTypeOptions`;
    return await get<IGetAllFileTypeOptionsApiResponse>(url);
  },

  // Files

  async getAllFiles(payload: IFetchFilesAllPagedThunkPayload): Promise<IHttpResponse<IGetAllFilesApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/List`;
    const {pagination, sortOrder, filter} = payload;

    const queryString = returnQueryStringWithParams(url, {
      ...pagination,
      ...sortOrder,
      ...filter,
    });
    return await get<IGetAllFilesApiResponse>(queryString);
  },

  // Resources

  async getFileById(id: string): Promise<IHttpResponse<IGetFileByIdApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/${id}/Details`;
    return await get<IGetFileByIdApiResponse>(url);
  },

  async getAllResources(payload: IFetchResourcesThunkPayload): Promise<IHttpResponse<IGetResourcesApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/List`;

    const queryString = returnQueryStringWithParams(url, {
      ...payload.filter,
    });
    return await get<IGetResourcesApiResponse>(queryString);
  },

  async deleteFileById(contentItemId: string): Promise<IHttpResponse<IResourceItemBase>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/${contentItemId}/Delete`;
    return await del<IResourceItemBase>(url, {});
  },

  async updateFileResource(
    payload: IUpdateFileResourcePayload
  ): Promise<IHttpResponse<IUpdateFileResourceApiResponse>> {
    const configHeaders = getApiMultipartFormDataHeadersConfig();
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/${ApiPathsEnum.FILE}/Update`;

    return axiosApiInstance.post(url, payload, configHeaders);
  },

  async updateScormResource(
    payload: IUpdateFileResourcePayload
  ): Promise<IHttpResponse<IUpdateFileResourceApiResponse>> {
    const configHeaders = getApiMultipartFormDataHeadersConfig();
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/${ApiPathsEnum.SCORM}/Update`;

    return axiosApiInstance.post(url, payload, configHeaders);
  },

  async updateWebLinkResource(
    payload: IUpdateWebLinkResourcePayload
  ): Promise<IHttpResponse<IUpdateWebLinkResourceApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/${ApiPathsEnum.WEB_LINK}/Update`;

    return await post<IUpdateWebLinkResourceApiResponse>(url, payload);
  },

  async downloadFileByResourceId(id: string): Promise<IHttpResponse<IGetFileByResourceIdApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/GetFileByResourceId`;
    const config = getApiJsonBlobDataHeadersConfig();
    const queryString = returnQueryStringWithParams(url, {
      id,
    });

    return await axiosApiInstance.get(queryString, config);
  },

  // Instruments

  async getAllInstruments(payload: IGetInstrumentsThunkPayload): Promise<IHttpResponse<IGetInstrumentsApiResponse>> {
    const url = `${apiConfig.baseUrlSurvey}/${ApiPathsEnum.INSTRUMENT}/GetInstrumentsForLearningPaths`;

    const queryString = returnQueryStringWithParams(url, {
      ...payload.filter,
    });
    return await get<IGetInstrumentsApiResponse>(queryString);
  },

  async createShortUrlExpiration(
    payload: ICreateShortUrlExpirationPayload
  ): Promise<IHttpResponse<ICreateShortUrlExpirationApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/CreateShortUrlExpiration`;
    console.log('=== API ENDPOINT DEBUG ===');
    console.log('Making API call to:', url);
    console.log('Full URL breakdown:');
    console.log('- Base URL PAM:', apiConfig.baseUrlPam);
    console.log('- Resources enum:', ApiPathsEnum.RESOURCES);
    console.log('- Full endpoint:', `/${ApiPathsEnum.RESOURCES}/CreateShortUrlExpiration`);
    console.log('Payload:', payload);

    // Check authentication token
    console.log('=== AUTHENTICATION DEBUG ===');
    try {
      const {getCurrentAccessToken} = await import('utils');
      const token = getCurrentAccessToken();
      console.log('Access token available:', !!token);
      console.log('Token length:', token ? token.length : 0);
      console.log('Token preview:', token ? `${token.substring(0, 20)}...` : 'No token');
    } catch (authError) {
      console.error('Error getting access token:', authError);
    }

    // Check subscription key from test API config
    const {jaEeTestApi} = config;
    console.log('Subscription key from test config:', jaEeTestApi.subscriptionKey);
    console.log('Subscription key available:', !!jaEeTestApi.subscriptionKey);

    console.log('=== TESTING POTENTIAL ENDPOINTS ===');
    console.log('Current endpoint being tried:', url);
    console.log('Alternative endpoints to consider:');
    console.log('- CreateShortUrl (without Expiration)');
    console.log('- ShortUrl/Create');
    console.log('- CreateExpirableLink');
    console.log('- GenerateShortUrl');
    console.log('- CreateTemporaryUrl');
    console.log('- Resources/ShortUrl');
    console.log('');
    try {
      console.log('=== MAKING API CALL ===');
      console.log('🔧 Using axiosApiInstance.post to ensure proper authentication headers');
      console.log('URL:', url);
      console.log('Payload:', JSON.stringify(payload, null, 2));
      console.log('Expected headers (will be added by interceptor):');
      console.log('- Content-Type: application/json');
      console.log('- Authorization: Bearer [token]');
      console.log('- Ocp-Apim-Subscription-Key: [subscription-key]');
      console.log('');
      console.log('📍 IMPORTANT: Check Network tab to verify actual request details');
      console.log('📍 Look for POST request to:', url);
      // Add request interceptor debugging
      console.log('=== AXIOS INSTANCE DEBUG ===');
      console.log('Axios instance defaults:', axiosApiInstance.defaults);
      console.log('Axios interceptors configured: Request interceptor should add auth headers');

      const result = await axiosApiInstance.post(url, payload);
      console.log('✅ API call successful:', result);

      // Transform axios response to match IHttpResponse format
      const transformedResponse: IHttpResponse<ICreateShortUrlExpirationApiResponse> = {
        ...new Response(),
        parsedBody: result.data,
        headers: result.headers,
        status: result.status,
        statusText: result.statusText,
        ok: result.status >= 200 && result.status < 300,
      };

      return transformedResponse;
    } catch (error) {
      console.error('=== API CALL FAILED ===');
      console.error('Error details:', error);
      console.error('Error type:', typeof error);

      // Check different error scenarios
      if (error && typeof error === 'object') {
        const errorObj = error as any;
        console.error('Error response status:', errorObj.response?.status);
        console.error('Error response headers:', errorObj.response?.headers);
        console.error('Error response body:', errorObj.response?.data);
        console.error('Error config URL:', errorObj.config?.url);
        console.error('Error config method:', errorObj.config?.method);
        console.error('Error config headers:', errorObj.config?.headers);

        if (errorObj.response?.status === 404) {
          console.error('❌ 404 Not Found - The CreateShortUrlExpiration endpoint does not exist');
          console.error('📊 INVESTIGATION RESULTS:');
          console.error('1. ✅ URL format appears correct:', url);
          console.error('2. ❌ Endpoint may not be deployed to test environment');
          console.error('3. ❌ Endpoint name may be incorrect');
          console.error('');
          console.error('🔍 TROUBLESHOOTING STEPS:');
          console.error('1. Check with backend team if endpoint exists');
          console.error('2. Try alternative endpoint names');
          console.error('3. Check API documentation');
          console.error('4. Verify endpoint is deployed to test environment');
          console.error('');
          console.error('🚀 NEXT ACTIONS:');
          console.error('- Contact backend team to confirm endpoint name');
          console.error('- Check if endpoint exists in different API service');
          console.error('- Verify API deployment status');
        } else if (errorObj.response?.status === 401) {
          console.error('❌ 401 Unauthorized - Authentication issue');
          console.error('🔍 Check:');
          console.error('1. Valid access token');
          console.error('2. Correct subscription key');
          console.error('3. User permissions');
        } else if (errorObj.response?.status === 403) {
          console.error('❌ 403 Forbidden - Permission issue');
          console.error('🔍 User may not have permission to create short URLs');
        }
      }

      throw error;
    }
  },

  async createShortenToUrlExpiration(
    payload: ICreateShortUrlExpirationPayload
  ): Promise<IHttpResponse<ICreateShortUrlExpirationApiResponse>> {
    const url = `${apiConfig.baseUrlPam}/${ApiPathsEnum.RESOURCES}/CreateShortenToUrlExpiration`;

    return axiosApiInstance
      .post(url, payload)
      .then((result) => {
        return {
          ...new Response(),
          parsedBody: result.data,
          headers: result.headers,
          status: result.status,
          statusText: result.statusText,
          ok: result.status >= 200 && result.status < 300,
        };
      })
      .catch((error) => {
        console.error('Failed to create expirable short URL:', error);
        throw error;
      });
  },
};
